import { useCallback, useRef, useState } from 'react'

type UseFetch = <T, Args extends unknown[]>(
  dataFetcher: (...args: any[]) => Promise<T>,
  options: {
    initialData?: T
    initialLoadingState?: boolean
    onlyReturnLatestFetch?: boolean
  },
) => {
  fetchData: (...args: Args) => Promise<T | undefined>
  isLoading: boolean
  error: Error | undefined
  response?: T
  hasError: boolean
}

const useFetch: UseFetch = (
  dataFetcher,
  { initialData, initialLoadingState = false, onlyReturnLatestFetch = false },
) => {
  const [isLoading, setIsLoading] = useState(initialLoadingState)
  const [hasError, setHasError] = useState(false)
  const [error, setError] = useState()
  const [response, setResponse] = useState(initialData)

  const requestCounterRef = useRef(0)
  const fetchData = useCallback(
    async (...args: any[]) => {
      const currentCount = requestCounterRef.current + 1
      requestCounterRef.current = currentCount
      setIsLoading(true)
      setHasError(false)
      let latestResponse = undefined
      try {
        latestResponse = await dataFetcher(...args)
        // If only latest request needs to be returned, i.e. older request needs to be ignored, then checking if current request is latest or not. if not then returning.
        if (onlyReturnLatestFetch && currentCount != requestCounterRef.current) {
          return
        }
        setResponse(latestResponse)
      } catch (e: any) {
        setError(e)
        setHasError(true)
      }
      setIsLoading(false)
      return latestResponse
    },
    [dataFetcher, onlyReturnLatestFetch],
  )

  return { fetchData, isLoading, error, response, hasError }
}

export default useFetch
